import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { createPortal } from "react-dom";
import MapGL, { Popup } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";

import {
  formatLocationLabel,
  summarizeClusterUsers,
} from "../utils/clientActivityGeo";

import { Shimmer } from "./MotionPrimitives";

const EMPTY_GEOJSON = {
  type: "FeatureCollection",
  features: [],
};

const LIGHT_MAP_STYLE =
  "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json";
const DARK_MAP_STYLE =
  "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";
const STATE_BORDERS_URL =
  "https://d2ad6b4ur7yvpq.cloudfront.net/naturalearth-3.3.0/ne_50m_admin_1_states_provinces_lines.geojson";

// Esri World Imagery — no API key required. Tile path is {z}/{y}/{x}.
const ESRI_SATELLITE_TILES =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const ESRI_LABEL_TILES =
  "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";
const ESRI_ATTRIBUTION =
  "Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community";

// Official Carto glyph host (Open Sans Regular/Bold are present here).
const CARTO_GLYPHS_URL =
  "https://tiles.basemaps.cartocdn.com/fonts/{fontstack}/{range}.pbf";

const USERS_SOURCE_ID = "users";
const STATE_SOURCE_ID = "state-borders";

const OVERLAY_LAYER_IDS = [
  "state-border-lines",
  "clusters",
  "cluster-count",
  "unclustered-point",
];

const BASEMAP_OPTIONS = [
  { id: "streets", label: "Streets", shortLabel: "Streets", icon: "fa-road" },
  { id: "dark", label: "Dark", shortLabel: "Dark", icon: "fa-moon" },
  {
    id: "satellite",
    label: "Satellite",
    shortLabel: "Sat",
    icon: "fa-globe",
  },
  {
    id: "hybrid",
    label: "Satellite + labels",
    shortLabel: "Hybrid",
    icon: "fa-map-marked-alt",
  },
];

function buildSatelliteStyle({ withLabels = false } = {}) {
  const sources = {
    "esri-satellite": {
      type: "raster",
      tiles: [ESRI_SATELLITE_TILES],
      tileSize: 256,
      maxzoom: 19,
      attribution: ESRI_ATTRIBUTION,
    },
  };
  const layers = [
    {
      id: "background",
      type: "background",
      paint: { "background-color": "#0b1220" },
    },
    {
      id: "esri-satellite",
      type: "raster",
      source: "esri-satellite",
    },
  ];

  if (withLabels) {
    sources["esri-labels"] = {
      type: "raster",
      tiles: [ESRI_LABEL_TILES],
      tileSize: 256,
      maxzoom: 19,
      attribution: ESRI_ATTRIBUTION,
    };
    layers.push({
      id: "esri-labels",
      type: "raster",
      source: "esri-labels",
    });
  }

  return {
    version: 8,
    name: withLabels ? "Satellite Hybrid" : "Satellite",
    glyphs: CARTO_GLYPHS_URL,
    sources,
    layers,
  };
}

const SATELLITE_STYLE = buildSatelliteStyle();
const HYBRID_STYLE = buildSatelliteStyle({ withLabels: true });

function getMapStyle(basemapId) {
  switch (basemapId) {
    case "dark":
      return DARK_MAP_STYLE;
    case "satellite":
      return SATELLITE_STYLE;
    case "hybrid":
      return HYBRID_STYLE;
    case "streets":
    default:
      return LIGHT_MAP_STYLE;
  }
}

function isSatelliteBasemap(basemapId) {
  return basemapId === "satellite" || basemapId === "hybrid";
}

function getStateBorderPaint(satelliteMode, isDarkMode) {
  return {
    "line-color": satelliteMode
      ? "#f8fafc"
      : isDarkMode
        ? "#64748b"
        : "#94a3b8",
    "line-opacity": [
      "interpolate",
      ["linear"],
      ["zoom"],
      3.5,
      satelliteMode ? 0.25 : 0.15,
      5,
      satelliteMode ? 0.7 : 0.55,
      8,
      satelliteMode ? 0.95 : 0.85,
    ],
    "line-width": [
      "interpolate",
      ["linear"],
      ["zoom"],
      3.5,
      0.35,
      6,
      1,
      9,
      1.8,
    ],
  };
}

const clusterLayer = {
  id: "clusters",
  type: "circle",
  source: USERS_SOURCE_ID,
  filter: ["has", "point_count"],
  paint: {
    "circle-radius": ["step", ["get", "point_count"], 22, 25, 30, 100, 40],
    "circle-color": [
      "step",
      ["get", "point_count"],
      "#3b82f6",
      25,
      "#f59e0b",
      100,
      "#ef4444",
    ],
    "circle-opacity": 0.95,
    "circle-stroke-width": 2,
    "circle-stroke-color": "#ffffff",
  },
};

const clusterCountLayer = {
  id: "cluster-count",
  type: "symbol",
  source: USERS_SOURCE_ID,
  filter: ["has", "point_count"],
  layout: {
    "text-field": ["get", "point_count_abbreviated"],
    "text-size": 13,
    "text-font": ["Open Sans Bold", "Open Sans Regular"],
    "text-allow-overlap": true,
  },
  paint: {
    "text-color": "#ffffff",
  },
};

const unclusteredPointLayer = {
  id: "unclustered-point",
  type: "circle",
  source: USERS_SOURCE_ID,
  filter: ["!", ["has", "point_count"]],
  paint: {
    "circle-radius": 8,
    "circle-color": [
      "match",
      ["get", "source"],
      "authenticated",
      "#22c55e",
      "registered",
      "#22c55e",
      "app",
      "#38bdf8",
      "#22c55e",
    ],
    "circle-opacity": 0.95,
    "circle-stroke-width": 2,
    "circle-stroke-color": "#ffffff",
  },
};

function bringOverlaysToFront(map) {
  if (!map?.getLayer) return;

  OVERLAY_LAYER_IDS.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      try {
        map.moveLayer(layerId);
      } catch (error) {
        // Style may still be swapping.
      }
    }
  });
}

function ensureOverlayLayers(
  map,
  geoJsonData,
  { satelliteMode, isDarkMode, updateData = true } = {}
) {
  if (!map?.getStyle?.()) return false;

  // Avoid racing addSource/addLayer while the basemap style is still loading.
  if (typeof map.isStyleLoaded === "function" && !map.isStyleLoaded()) {
    return false;
  }

  try {
    if (!map.getSource(STATE_SOURCE_ID)) {
      map.addSource(STATE_SOURCE_ID, {
        type: "geojson",
        data: STATE_BORDERS_URL,
      });
    }

    if (!map.getLayer("state-border-lines")) {
      map.addLayer({
        id: "state-border-lines",
        type: "line",
        source: STATE_SOURCE_ID,
        minzoom: 3.5,
        paint: getStateBorderPaint(satelliteMode, isDarkMode),
      });
    } else {
      const paint = getStateBorderPaint(satelliteMode, isDarkMode);

      Object.entries(paint).forEach(([key, value]) => {
        map.setPaintProperty("state-border-lines", key, value);
      });
    }

    if (!map.getSource(USERS_SOURCE_ID)) {
      map.addSource(USERS_SOURCE_ID, {
        type: "geojson",
        data: geoJsonData,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
    } else if (updateData) {
      map.getSource(USERS_SOURCE_ID).setData(geoJsonData);
    }

    if (!map.getLayer("clusters")) {
      map.addLayer(clusterLayer);
    }

    if (!map.getLayer("cluster-count")) {
      map.addLayer(clusterCountLayer);
    }

    if (!map.getLayer("unclustered-point")) {
      map.addLayer(unclusteredPointLayer);
    }

    bringOverlaysToFront(map);

    return true;
  } catch (error) {
    console.warn("Failed to ensure live map overlays", error);

    return false;
  }
}

function MapTooltip({ tooltip }) {
  const primaryUser = tooltip.users[0];
  const locationLabel = formatLocationLabel(
    primaryUser?.city,
    primaryUser?.region
  );
  const { deviceCounts, lastActivityLabel } = summarizeClusterUsers(
    tooltip.users
  );

  return (
    <div className="min-w-[180px] max-w-[min(260px,70vw)] text-sm text-gray-900">
      <p className="font-semibold text-gray-900">{locationLabel}</p>
      <p className="mt-1 text-gray-700">
        {tooltip.count} live user{tooltip.count > 1 ? "s" : ""}
      </p>

      {deviceCounts.length > 0 && (
        <div className="mt-2 sm:mt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Devices
          </p>
          <ul className="mt-1 space-y-1">
            {deviceCounts.slice(0, 5).map(([device, count]) => (
              <li
                className="flex items-center justify-between gap-3 text-gray-700"
                key={device}
              >
                <span className="truncate">{device}</span>
                <span className="font-medium tabular-nums">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tooltip.count === 1 && primaryUser?.appVersion && (
        <p className="mt-2 text-xs text-gray-500">
          App v{primaryUser.appVersion}
        </p>
      )}

      {lastActivityLabel && (
        <p className="mt-2 text-xs text-gray-500 sm:mt-3">
          Last activity: {lastActivityLabel}
        </p>
      )}

      {tooltip.count > tooltip.users.length && (
        <p className="mt-2 text-xs text-gray-500">
          +{tooltip.count - tooltip.users.length} more in this cluster
        </p>
      )}
    </div>
  );
}

function MapLegend({
  featureCount,
  authenticatedCount,
  appCount,
  summary,
  variant = "admin",
}) {
  const isReseller = variant === "reseller";
  const hasSummary = !isReseller && summary;
  const liveTotal = hasSummary ? summary.total : featureCount;
  const mappedCount = hasSummary ? summary.mapped : featureCount;
  const unmappedCount = hasSummary ? summary.unmapped : 0;
  const authenticatedTotal = hasSummary
    ? summary.authenticated
    : authenticatedCount;
  const noAuthTotal = hasSummary ? summary.noAuth : appCount;

  return (
    <div className="absolute left-2 top-2 max-w-[calc(100%-3.5rem)] rounded-lg bg-white/90 px-2 py-1.5 text-[11px] shadow-sm ring-1 ring-gray-200/70 backdrop-blur-sm dark:bg-gray-900/85 dark:ring-white/10 sm:left-3 sm:top-3 sm:max-w-none sm:px-3 sm:py-2 sm:text-xs">
      <p className="font-semibold text-gray-900 dark:text-white">
        <span className="sm:hidden">
          {isReseller ? "Your map" : "Live map"}
        </span>
        <span className="hidden sm:inline">
          {isReseller ? "Your playlist devices" : "Live client map"}
        </span>
      </p>
      <p className="tabular-nums text-gray-600 dark:text-gray-300">
        {isReseller
          ? `${featureCount} device session${featureCount === 1 ? "" : "s"} mapped`
          : `${liveTotal} live client${liveTotal === 1 ? "" : "s"}`}
      </p>
      {hasSummary && (
        <p className="tabular-nums text-[10px] text-gray-500 dark:text-gray-400 sm:text-[11px]">
          {mappedCount} mapped
          {unmappedCount > 0 ? ` · ${unmappedCount} location unavailable` : ""}
        </p>
      )}
      {liveTotal > 0 && !isReseller && (
        <div className="mt-1.5 space-y-1 text-[10px] text-gray-500 dark:text-gray-400 sm:mt-2 sm:text-[11px]">
          <p className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
            {authenticatedTotal} authenticated
          </p>
          <p className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-sky-400" />
            {noAuthTotal} no-auth client{noAuthTotal === 1 ? "" : "s"}
          </p>
        </div>
      )}
      {featureCount > 0 && isReseller && (
        <p className="mt-1.5 text-[10px] text-gray-500 dark:text-gray-400 sm:mt-2 sm:text-[11px]">
          Only devices on playlists you manage
        </p>
      )}
    </div>
  );
}

function MapBasemapControl({ basemap, onChange }) {
  return (
    <div
      aria-label="Map basemap"
      className="absolute bottom-8 left-2 z-10 flex max-w-[calc(100%-1rem)] flex-wrap gap-1 rounded-lg bg-white/90 p-1 shadow-sm ring-1 ring-gray-200/70 backdrop-blur-sm dark:bg-gray-900/85 dark:ring-white/10 sm:bottom-10 sm:left-3"
      role="group"
    >
      {BASEMAP_OPTIONS.map((option) => {
        const isActive = basemap === option.id;

        return (
          <button
            aria-pressed={isActive}
            className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-medium transition-colors sm:px-2.5 ${
              isActive
                ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
            }`}
            key={option.id}
            title={option.label}
            type="button"
            onClick={() => onChange(option.id)}
          >
            <i className={`fas ${option.icon} text-[10px]`} />
            <span className="sm:hidden">{option.shortLabel}</span>
            <span className="hidden sm:inline">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function LiveUserMap({
  data,
  isDarkMode = false,
  loading = false,
  showFullscreenControl = true,
  variant = "admin",
}) {
  const mapRef = useRef(null);
  const geoJsonRef = useRef(EMPTY_GEOJSON);
  const [tooltip, setTooltip] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [basemap, setBasemap] = useState(isDarkMode ? "dark" : "streets");
  const featureCount = data?.features?.length || 0;
  const authenticatedCount =
    data?.features?.filter(
      (feature) =>
        feature.properties?.source === "authenticated" ||
        feature.properties?.source === "registered"
    ).length || 0;
  const appCount =
    data?.features?.filter((feature) => feature.properties?.source === "app")
      .length || 0;
  const mapStyle = useMemo(() => getMapStyle(basemap), [basemap]);
  const satelliteMode = isSatelliteBasemap(basemap);
  const geoJsonData = data?.type === "FeatureCollection" ? data : EMPTY_GEOJSON;

  geoJsonRef.current = geoJsonData;

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((current) => !current);
  }, []);

  const syncOverlays = useCallback(
    ({ updateData = true, resize = true } = {}) => {
      const map = mapRef.current?.getMap?.();

      if (!map) return;

      ensureOverlayLayers(map, geoJsonRef.current, {
        satelliteMode,
        isDarkMode,
        updateData,
      });

      if (resize) {
        map.resize();
      }
    },
    [isDarkMode, satelliteMode]
  );

  useEffect(() => {
    setBasemap((current) => {
      if (current === "streets" || current === "dark") {
        return isDarkMode ? "dark" : "streets";
      }

      return current;
    });
  }, [isDarkMode]);

  useEffect(() => {
    if (!isFullscreen) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeFullscreen();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeFullscreen, isFullscreen]);

  useEffect(() => {
    const map = mapRef.current?.getMap?.();

    if (!map) return undefined;

    const installOverlays = () => {
      syncOverlays({ updateData: true, resize: true });
    };

    const onStyleData = () => {
      if (!map.getLayer("clusters") || !map.getSource(USERS_SOURCE_ID)) {
        syncOverlays({ updateData: true, resize: false });
      } else {
        bringOverlaysToFront(map);
      }
    };

    const onResize = () => {
      map.resize();
      bringOverlaysToFront(map);
    };

    map.on("load", installOverlays);
    map.on("style.load", installOverlays);
    map.on("styledata", onStyleData);
    installOverlays();

    const frame = window.requestAnimationFrame(installOverlays);
    const timeout = window.setTimeout(installOverlays, 150);
    const lateTimeout = window.setTimeout(installOverlays, 400);
    const container = map.getContainer?.()?.parentElement;
    let observer;

    if (container && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(onResize);
      observer.observe(container);
    }

    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return () => {
      map.off("load", installOverlays);
      map.off("style.load", installOverlays);
      map.off("styledata", onStyleData);
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      window.clearTimeout(lateTimeout);
      observer?.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [basemap, isFullscreen, loading, syncOverlays]);

  useEffect(() => {
    syncOverlays({ updateData: true, resize: false });
  }, [geoJsonData, syncOverlays]);

  async function showFeatureTooltip(feature) {
    if (!feature) {
      setTooltip(null);

      return;
    }

    const map = mapRef.current?.getMap();

    if (!map) return;

    const { geometry } = feature;
    const [longitude, latitude] = geometry.coordinates;

    if (feature.properties?.cluster) {
      const clusterId = feature.properties.cluster_id;
      const count = feature.properties.point_count;
      const source = map.getSource(USERS_SOURCE_ID);

      if (!source?.getClusterLeaves) {
        setTooltip(null);

        return;
      }

      const leaves = await source.getClusterLeaves(clusterId, 20, 0);

      setTooltip({
        longitude,
        latitude,
        count,
        users: leaves.map((leaf) => leaf.properties),
      });

      return;
    }

    setTooltip({
      longitude,
      latitude,
      count: 1,
      users: [feature.properties],
    });
  }

  async function handleMouseMove(event) {
    await showFeatureTooltip(event.features?.[0]);
  }

  function handleMouseLeave() {
    setTooltip(null);
  }

  async function handleClick(event) {
    const feature = event.features?.[0];

    if (!feature) {
      setTooltip(null);

      return;
    }

    await showFeatureTooltip(feature);
  }

  if (loading) {
    return (
      <div className="h-full min-h-[280px] overflow-hidden rounded-xl ring-1 ring-gray-200/60 dark:ring-white/[0.06]">
        <Shimmer className="h-full w-full" />
      </div>
    );
  }

  const mapContent = (
    <div
      className={
        isFullscreen
          ? "fixed inset-0 z-[220] bg-gray-950/95 p-2 sm:p-5"
          : "relative h-full min-h-[280px] w-full"
      }
    >
      <div
        className={`overflow-hidden ring-1 ring-gray-300/70 dark:ring-white/[0.06] ${
          isFullscreen
            ? "relative h-full rounded-2xl"
            : "absolute inset-0 rounded-xl"
        }`}
      >
        <MapGL
          initialViewState={{
            longitude: -98.5795,
            latitude: 39.8283,
            zoom: 3.6,
          }}
          interactiveLayerIds={["clusters", "unclustered-point"]}
          key={basemap}
          mapStyle={mapStyle}
          ref={mapRef}
          style={{ width: "100%", height: "100%" }}
          onClick={handleClick}
          onLoad={() => syncOverlays({ updateData: true, resize: true })}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
        >
          {tooltip && (
            <Popup
              anchor="top"
              closeButton={false}
              closeOnClick={false}
              latitude={tooltip.latitude}
              longitude={tooltip.longitude}
            >
              <MapTooltip tooltip={tooltip} />
            </Popup>
          )}
        </MapGL>

        <MapLegend
          appCount={appCount}
          authenticatedCount={authenticatedCount}
          featureCount={featureCount}
          summary={data?.summary}
          variant={variant}
        />

        <MapBasemapControl basemap={basemap} onChange={setBasemap} />

        {showFullscreenControl && (
          <div className="absolute right-2 top-2 flex items-center gap-2 sm:right-3 sm:top-3">
            {isFullscreen && (
              <span className="hidden rounded-lg bg-white/90 px-2.5 py-1.5 text-[11px] text-gray-500 ring-1 ring-gray-200/70 backdrop-blur-sm dark:bg-gray-900/85 dark:text-gray-400 dark:ring-white/10 sm:inline">
                Press Esc to exit
              </span>
            )}
            <button
              aria-label={
                isFullscreen ? "Exit fullscreen map" : "Open fullscreen map"
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/90 text-gray-700 shadow-sm ring-1 ring-gray-200/70 transition-colors hover:bg-white dark:bg-gray-900/85 dark:text-gray-200 dark:ring-white/10 dark:hover:bg-gray-900 sm:h-9 sm:w-9"
              type="button"
              onClick={toggleFullscreen}
            >
              <i
                className={`fas ${isFullscreen ? "fa-compress" : "fa-expand"} text-sm`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (isFullscreen) {
    return createPortal(mapContent, document.body);
  }

  return mapContent;
}

export default LiveUserMap;
